import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

interface PdfAttachmentPreviewProps {
  dataUrl: string;
  name: string;
}

const dataUrlToBytes = (dataUrl: string) => {
  const encoded = dataUrl.split(',', 2)[1];
  if (!encoded) throw new Error('รูปแบบไฟล์ PDF ไม่ถูกต้อง');
  const binary = window.atob(encoded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

function PdfPage({ document, pageNumber }: { document: PDFDocumentProxy; pageNumber: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    let renderTask: ReturnType<Awaited<ReturnType<PDFDocumentProxy['getPage']>>['render']> | undefined;

    const render = async () => {
      const page = await document.getPage(pageNumber);
      if (cancelled || !canvasRef.current) return;
      const initialViewport = page.getViewport({ scale: 1 });
      const availableWidth = Math.min(900, Math.max(280, window.innerWidth - 48));
      const cssScale = Math.min(1.6, availableWidth / initialViewport.width);
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: cssScale * pixelRatio });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('เบราว์เซอร์ไม่สามารถสร้างพื้นที่แสดง PDF ได้');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      canvas.style.width = `${Math.ceil(viewport.width / pixelRatio)}px`;
      canvas.style.height = `${Math.ceil(viewport.height / pixelRatio)}px`;
      renderTask = page.render({ canvas, canvasContext: context, viewport });
      await renderTask.promise;
    };

    void render();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [document, pageNumber]);

  return <figure className="pdf-attachment-page">
    <canvas ref={canvasRef} aria-label={`หน้า ${pageNumber}`} />
    <figcaption>หน้า {pageNumber}</figcaption>
  </figure>;
}

export function PdfAttachmentPreview({ dataUrl, name }: PdfAttachmentPreviewProps) {
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let disposed = false;
    let loadingTask: PDFDocumentLoadingTask | undefined;
    const load = async () => {
      const { GlobalWorkerOptions, getDocument } = await import('pdfjs-dist');
      if (disposed) return;
      GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      loadingTask = getDocument({ data: dataUrlToBytes(dataUrl) });
      const loadedDocument = await loadingTask.promise;
      if (!disposed) setDocument(loadedDocument);
    };
    void load().catch(() => {
      if (!disposed) setError('เปิดตัวอย่าง PDF ไม่สำเร็จ ไฟล์อาจเสียหายหรือมีรหัสผ่าน');
    });
    return () => {
      disposed = true;
      void loadingTask?.destroy();
    };
  }, [dataUrl]);

  if (error) return <div className="attachment-unsupported" role="alert"><strong>{error}</strong></div>;
  if (!document) return <div className="pdf-attachment-loading" role="status"><span className="spin" /> กำลังเปิด {name}…</div>;

  return <div className="pdf-attachment-pages">
    {Array.from({ length: document.numPages }, (_, index) => <PdfPage document={document} pageNumber={index + 1} key={index + 1} />)}
  </div>;
}
